import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { ChatbotConfig, TrainingSource, IMessage } from '../types/types';
import { ChatbotBuilder, DataCollectionField } from '../types/chatbotbuilder.type';

interface ChatbotStore extends ChatbotConfig {
  updateBotName: (name: string) => void;
  updateBotIcon: (icon: string) => void;
  updateBotLogo: (logo: string) => void;
  updateWelcomeTitle: (title: string) => void;
  updateWelcomeMessage: (message: string) => void;
  updateInputPlaceholder: (placeholder: string) => void;
  addQuestion: (question: string) => void;
  removeQuestion: (index: number) => void;
  updateAiPrompt: (prompt: string) => void;
  updateAiModel: (model: string) => void;
  updateAiRole: (role: string) => void;
  updateTheme: (theme: string) => void;
  updateColors: (primary: string, secondary: string, text?: string) => void;
  updateSize: (size: string) => void;
  updatePosition: (position: string) => void;
  updateWindowStyle: (style: string) => void;
  addDataField: (field: DataCollectionField) => void;
  removeDataField: (id: string) => void;
  addTrainingSource: (source: TrainingSource) => void;
  removeTrainingSource: (id: string) => void;
  setCurrentStep: (step: number) => void;
  nextStep: () => void;
  prevStep: () => void;
  setChatbotData: (data: ChatbotBuilder) => void;
  updateDataCompletionCompleted: (dcc: boolean) => void;
  updateMessagesHistory: (messages: IMessage[]) => void;
  updateLiveChatRoundRobinEnabled: (enabled: boolean) => void;
  updateLiveChatRoundRobinSources: (
    sources: Array<'inbound' | 'meta_ctwa_ad' | 'website_chatbot' | 'manual' | 'imported'>
  ) => void;
  updateLiveChatRoundRobinOnlyOnlineAgents: (enabled: boolean) => void;
  isLoadingApiCall:boolean;
  isSidebarOpen: boolean;
  setSidebarOpen: (isOpen: boolean) => void;
  setConversionStatus: (conversionStatus: string) => void;
  toggleSidebar: () => void;
  conversionStatus?:string;
  conversionId?:string | null;
  setConversionId: (conversionId: string | null) => void;
}


export const useChatbotStore = create<ChatbotStore>()(
  persist(
    (set) => ({
      botName: '',
      botIcon: 'bot',
      botLogo: '',
      welcomeTitle: 'Hi there! 👋',
      welcomeMessage: 'I\'m the AI assistant for IANA Example Domains. We manage domains like example.com for documentation purposes. I help you understand the use and limitations of these domains.',
      inputPlaceholder: 'Type your message...',
      questionList: [],
      aiPrompt: ``,
      aiModel: 'GPT-4o',
      aiRole: 'Customer Support Agent',
      primaryColor: '#2563eb',
      secondaryColor: '#1e40af',
      textColor: '#ffffff',
      size: 'medium',
      position: 'right',
      windowStyle: 'floating',
      theme: 'blue',
      currentStep: 0,
      dataFields: [],
      trainingSources: [],
      dataCompletionCompleted: false,
      messagesHistory: [],
      liveChatRoundRobinEnabled: false,
      liveChatRoundRobinSources: [],
      liveChatRoundRobinOnlyOnlineAgents: false,
      updateBotName: (name) => set({ botName: name }),
      updateBotIcon: (icon) => set({ botIcon: icon }),
      updateBotLogo: (logo) => set({ botLogo: logo }),
      updateWelcomeTitle: (title) => set({ welcomeTitle: title }),
      updateWelcomeMessage: (message) => set({ welcomeMessage: message }),
      updateInputPlaceholder: (placeholder) => set({ inputPlaceholder: placeholder }),
      addQuestion: (question) => set((state) => ({ questionList: [...state.questionList, question] })),
      removeQuestion: (index) => set((state) => ({ questionList: state.questionList.filter((_, i) => i !== index) })),
      updateAiPrompt: (prompt) => set({ aiPrompt: prompt }),
      updateAiModel: (model) => set({ aiModel: model }),
      updateAiRole: (role) => set({ aiRole: role }),
      updateTheme: (theme) => set({ theme }),
      updateColors: (primary, secondary, text) => set({ primaryColor: primary, secondaryColor: secondary, textColor: text || '#ffffff' }),
      updateSize: (size) => set({ size }),
      updatePosition: (position) => set({ position }),
      updateWindowStyle: (style) => set({ windowStyle: style }),
      addDataField: (field) => set((state) => ({ dataFields: [...state.dataFields, field] })),
      removeDataField: (id) => set((state) => ({ dataFields: state.dataFields.filter((f) => f._id !== id) })),
      addTrainingSource: (source) => set((state) => ({ trainingSources: [...state.trainingSources, source] })),
      removeTrainingSource: (id) => set((state) => ({ trainingSources: state.trainingSources.filter((s) => s.id !== id) })),
      setCurrentStep: (step) => set({ currentStep: step }),
      nextStep: () => set((state) => ({ currentStep: Math.min(state.currentStep + 1, 5) })),
      prevStep: () => set((state) => ({ currentStep: Math.max(state.currentStep - 1, 0) })),
      updateDataCompletionCompleted: (ddc) => set({ dataCompletionCompleted: ddc }),
      updateLiveChatRoundRobinEnabled: (enabled) => set({ liveChatRoundRobinEnabled: enabled }),
      updateLiveChatRoundRobinSources: (sources) => set({ liveChatRoundRobinSources: sources }),
      updateLiveChatRoundRobinOnlyOnlineAgents: (enabled) =>
        set({ liveChatRoundRobinOnlyOnlineAgents: enabled }),
      setChatbotData: (data) => set({
        botName: data.chatbot.name,
        botIcon: 'bot',
        botLogo: data.chatbot.logo,
        welcomeTitle: data.config.welcomeTitle,
        welcomeMessage: data.config.welcomeMessage,
        inputPlaceholder: data.config.inputPlaceholder,
        questionList: data.config.questionList || [],
        liveChatRoundRobinEnabled: data.config.liveChat?.roundRobin?.enabled ?? false,
        liveChatRoundRobinSources: data.config.liveChat?.roundRobin?.sourceFilters || [],
        liveChatRoundRobinOnlyOnlineAgents:
          data.config.liveChat?.roundRobin?.onlyOnlineAgents ?? false,
        aiPrompt: data.chatbot.assistantInstructions,
        aiModel: data.config.modelName,
        aiRole: data.chatbot.roleId,
        theme: data.config.theme,
        primaryColor: data.config.primaryColor,
        secondaryColor: data.config.secondaryColor,
        textColor: data.config.textColor,
        size: data.config.size,
        position: data.config.position,
        windowStyle: data.config.windowStyle,
        dataFields: data.dataCollection.map(field => ({
          _id: field._id,
          name: field.name,
          label: field.label,
          description: field.description || '',
          type: field.type,
          required: field.required
        })) as any,
        trainingSources: [], // Need to map if available
        dataCompletionCompleted: data.dataCompletionCompleted,
        messagesHistory: data.messages,
        conversionId: data.conversationId || null,
        conversionStatus: data.conversationStatus || 'bot_active',
      }),
      updateMessagesHistory: (messages) => set({ messagesHistory: messages }),
      isLoadingApiCall:false,
      isSidebarOpen: true,
      setSidebarOpen: (isOpen) => set({ isSidebarOpen: isOpen }),
      toggleSidebar: () => set((state) => ({ isSidebarOpen: !state.isSidebarOpen })),
      setConversionStatus: (conversionStatus) => set({ conversionStatus: conversionStatus }),
      setConversionId: (conversionId) => set({ conversionId: conversionId }),
      conversionId: null,
      conversionStatus:'bot_active',
    }),
    {
      name: 'chatbot-store',
      partialize: (state) => ({ 
        currentStep: state.currentStep,
        isSidebarOpen: state.isSidebarOpen,
        // You can add more fields to persist here if needed
        // e.g., theme: state.theme,
      }),
    }
  )
);
